import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { cn } from '@/shared/lib/cn';
import { formatMoney } from '@/shared/utils/money';
import { formatDateTime } from '../utils/paymentHelpers';
import { getAdminMyFatoorahTransactionDetails } from '../services/adminMyfatoorahTransactions.api';
import { MyFatoorahTransactionStatusBadge } from './MyFatoorahTransactionStatusBadge';

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <span className="text-xs font-medium text-muted-foreground shrink-0">{label}</span>
      <span className="text-sm text-foreground break-words text-end sm:max-w-[65%]">
        {value?.trim() ? value : '—'}
      </span>
    </div>
  );
}

function StatusPill({
  label,
  tone,
}: {
  label: string;
  tone: 'green' | 'amber' | 'rose' | 'slate';
}) {
  const cls =
    tone === 'green'
      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
      : tone === 'amber'
        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
        : tone === 'rose'
          ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
          : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';

  return (
    <span
      className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', cls)}
    >
      {label}
    </span>
  );
}

interface Props {
  paymentId: string | null;
  locale: string;
  onClose: () => void;
}

export function MyFatoorahTransactionDetailDrawer({ paymentId, locale, onClose }: Props) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'payments', 'myfatoorah', 'transactions', 'details', paymentId],
    queryFn: () => getAdminMyFatoorahTransactionDetails(paymentId!),
    enabled: Boolean(paymentId),
  });

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    if (paymentId) panelRef.current?.focus();
  }, [paymentId]);

  const waredTone = useMemo(() => {
    if (!data) return 'slate' as const;
    return data.wared_sync_status === 'success'
      ? ('green' as const)
      : data.wared_sync_status === 'failed'
        ? ('rose' as const)
        : ('amber' as const);
  }, [data]);

  if (!paymentId) return null;

  return (
    <div className="fixed inset-0 z-50 flex" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
        aria-label={t('common.close')}
        onClick={onClose}
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mf-tx-detail-title"
        className={cn(
          'relative ms-auto flex h-full w-full max-w-lg flex-col border-s border-border bg-background shadow-xl',
          'animate-in slide-in-from-end duration-200',
        )}
      >
        <header className="flex items-start justify-between gap-3 border-b border-border px-4 py-4 sm:px-5">
          <div className="min-w-0 space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('payments.myfatoorah.eyebrow')}
            </p>
            <h2 id="mf-tx-detail-title" className="text-lg font-semibold text-foreground truncate">
              {t('payments.myfatoorah.drawer.title')}
            </h2>
            {data ? (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <MyFatoorahTransactionStatusBadge status={data.status} />
              </div>
            ) : null}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label={t('common.close')}>
            <Icons.X className="h-5 w-5" />
          </Button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5 space-y-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : error || !data ? (
            <div className="rounded-lg border border-border bg-card p-4 text-sm text-destructive">
              {t('payments.myfatoorah.error.detailsLoadFailed')}
            </div>
          ) : (
            <>
              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">
                  {t('payments.myfatoorah.sections.customer')}
                </h3>
                <div className="rounded-lg border border-border bg-card p-3 space-y-2.5">
                  <DetailRow
                    label={t('payments.identity.customerName')}
                    value={data.customer_name}
                  />
                  <DetailRow
                    label={t('payments.identity.customerEmail')}
                    value={data.customer_email}
                  />
                </div>
              </section>

              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">
                  {t('payments.myfatoorah.sections.payment')}
                </h3>
                <div className="rounded-lg border border-border bg-card p-3 space-y-2.5">
                  <DetailRow
                    label={t('payments.amount')}
                    value={formatMoney(data.amount, data.currency)}
                  />
                  <DetailRow label={t('payments.currency')} value={data.currency} />
                  <DetailRow
                    label={t('payments.status.label')}
                    value={t(`payments.myfatoorah.status.${data.status}`, data.status)}
                  />
                </div>
              </section>

              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">
                  {t('payments.myfatoorah.sections.myfatoorah')}
                </h3>
                <div className="rounded-lg border border-border bg-card p-3 space-y-2.5">
                  <DetailRow
                    label={t('payments.myfatoorah.fields.invoiceId')}
                    value={data.provider_invoice_id}
                  />
                  <DetailRow
                    label={t('payments.myfatoorah.fields.providerPaymentId')}
                    value={data.provider_payment_id}
                  />
                </div>
              </section>

              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">
                  {t('payments.myfatoorah.sections.subscription')}
                </h3>
                <div className="rounded-lg border border-border bg-card p-3 space-y-2.5">
                  <DetailRow
                    label={t('payments.identity.subscriptionId')}
                    value={data.subscription_id}
                  />
                  <DetailRow label={t('payments.identity.tenantId')} value={data.tenant_id} />
                  <DetailRow
                    label={t('payments.myfatoorah.fields.checkoutSessionId')}
                    value={data.checkout_session_id}
                  />
                </div>
              </section>

              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">
                  {t('payments.myfatoorah.sections.callback')}
                </h3>
                <div className="rounded-lg border border-border bg-card p-3 space-y-2.5">
                  <DetailRow
                    label={t('payments.myfatoorah.fields.callbackStatus')}
                    value={t(
                      `payments.myfatoorah.callback.${data.callback_status}`,
                      data.callback_status,
                    )}
                  />
                  <DetailRow
                    label={t('payments.myfatoorah.fields.callbackTimestamp')}
                    value={formatDateTime(data.callback_received_time, locale)}
                  />
                </div>
              </section>

              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">
                  {t('payments.myfatoorah.sections.waredSync')}
                </h3>
                <div className="rounded-lg border border-border bg-card p-3 space-y-2.5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-medium text-muted-foreground">
                      {t('payments.myfatoorah.fields.syncStatus')}
                    </span>
                    <StatusPill
                      label={t(
                        `payments.myfatoorah.sync.${data.wared_sync_status}`,
                        data.wared_sync_status,
                      )}
                      tone={waredTone}
                    />
                  </div>
                  <DetailRow
                    label={t('payments.myfatoorah.fields.syncAttempts')}
                    value={String(data.wared_sync_attempts)}
                  />
                  {data.wared_sync_status === 'failed' ? (
                    <DetailRow
                      label={t('payments.myfatoorah.fields.syncLastError')}
                      value={data.wared_sync_last_error}
                    />
                  ) : null}
                </div>
              </section>

              <section className="rounded-lg border border-dashed border-border bg-muted/10 p-3 space-y-2 min-w-0">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('payments.myfatoorah.sections.technical')}
                </h3>
                <DetailRow label={t('payments.identity.paymentId')} value={data.payment_id} />
                <DetailRow
                  label={t('payments.myfatoorah.fields.createdAt')}
                  value={formatDateTime(data.created_at, locale)}
                />
                <DetailRow
                  label={t('payments.myfatoorah.fields.updatedAt')}
                  value={formatDateTime(data.updated_at, locale)}
                />
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
